import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Dialog } from '@headlessui/react';
import { Button } from 'components/Button';
import { Icon } from 'components/Icon';
import { Modal } from 'components/Modal';
import { commonStrings, lineStrings } from 'constants/strings';
import { ECardTypeTemplateId } from 'types/cardTypeMessage';
import {
  CardTypeMessageSortableCard,
  getCardLabel,
  MobileDragOverlayCard,
} from './CardTypeMessageSortableCard';
import type { TCardTypeSortableCard } from './CardTypeMessageSortableCard';

export type { TCardTypeSortableCard } from './CardTypeMessageSortableCard';

const strings = lineStrings.management.cardTypeMessage;

type Props = {
  open: boolean;
  templateId: ECardTypeTemplateId;
  cards: TCardTypeSortableCard[];
  disabled?: boolean;
  onClose: () => void;
  onApply: (cardIds: number[]) => void;
};

export function reorderSortableCards(
  cards: TCardTypeSortableCard[],
  activeId: number,
  overId: number,
): TCardTypeSortableCard[] {
  const oldIndex = cards.findIndex((card) => card.id === activeId);
  const newIndex = cards.findIndex((card) => card.id === overId);
  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return cards;
  return arrayMove(cards, oldIndex, newIndex);
}

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 639px)').matches,
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;

    const mediaQuery = window.matchMedia('(max-width: 639px)');
    const updateIsMobile = () => setIsMobile(mediaQuery.matches);
    updateIsMobile();
    mediaQuery.addEventListener('change', updateIsMobile);
    return () => mediaQuery.removeEventListener('change', updateIsMobile);
  }, []);

  return isMobile;
}

export function CardTypeMessageReorderModal({
  open,
  templateId,
  cards,
  disabled = false,
  onClose,
  onApply,
}: Props) {
  const [pendingCards, setPendingCards] = useState(cards);
  const [activeCardId, setActiveCardId] = useState<number | null>(null);
  const [previewCardId, setPreviewCardId] = useState<number | null>(null);
  const [moveAnnouncement, setMoveAnnouncement] = useState('');
  const isMobile = useIsMobile();
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    if (!open) return;
    setPendingCards(cards);
    setActiveCardId(null);
    setPreviewCardId(null);
    setMoveAnnouncement('');
  }, [cards, open]);

  useEffect(() => {
    if (isMobile) return;
    setPreviewCardId(null);
  }, [isMobile]);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveCardId(null);
    if (!over) return;
    setPendingCards((current) =>
      reorderSortableCards(current, Number(active.id), Number(over.id)),
    );
  };

  const handleMoveToPosition = (cardId: number, position: number) => {
    const activeIndex = pendingCards.findIndex((card) => card.id === cardId);
    const targetIndex = position - 1;
    if (
      activeIndex < 0 ||
      targetIndex < 0 ||
      targetIndex >= pendingCards.length ||
      activeIndex === targetIndex
    ) {
      return;
    }

    const activeCard = pendingCards[activeIndex]!;
    setPendingCards(arrayMove(pendingCards, activeIndex, targetIndex));
    setMoveAnnouncement(
      `${getCardLabel(activeCard.card, activeIndex)}を${position}${strings.reorderPositionLabel}に移動しました。`,
    );
  };

  const getPositionLabel = (id: string | number) => {
    const index = pendingCards.findIndex((card) => card.id === Number(id));
    return index < 0
      ? strings.cardTab
      : getCardLabel(pendingCards[index]!.card, index);
  };

  const activeCardIndex = pendingCards.findIndex(
    (card) => card.id === activeCardId,
  );
  const activeCard =
    activeCardIndex < 0 ? null : pendingCards[activeCardIndex]!;

  return (
    <Modal open={open} onClose={onClose} size="xl" mobileFullScreen>
      <div className="relative flex h-[100dvh] max-h-[100dvh] flex-col sm:h-auto sm:max-h-[min(88dvh,760px)]">
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-neutral-200 p-4 pb-3 sm:p-0 sm:pb-4">
          <div>
            <Dialog.Title
              as="h2"
              className="text-base font-bold text-neutral-900"
            >
              {strings.reorderModalTitle}
            </Dialog.Title>
            <Dialog.Description className="mt-1 text-sm text-neutral-600">
              {strings.reorderModalDescription}
            </Dialog.Description>
          </div>
          <button
            type="button"
            className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-100"
            aria-label={commonStrings.close}
            onClick={onClose}
          >
            <Icon icon="close" width={20} height={20} />
          </button>
        </div>

        <p className="shrink-0 px-4 py-2 text-xs text-neutral-500 sm:px-0 sm:py-3">
          {strings.reorderModalHint}
        </p>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={({ active }) => {
            setActiveCardId(Number(active.id));
            setPreviewCardId(null);
          }}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveCardId(null)}
          accessibility={{
            screenReaderInstructions: {
              draggable: strings.reorderModalHint,
            },
            announcements: {
              onDragStart: ({ active }) =>
                `${getPositionLabel(active.id)}を持ち上げました。`,
              onDragOver: ({ over }) =>
                over
                  ? `${getPositionLabel(over.id)}の位置へ移動しました。`
                  : '並び替え範囲の外へ移動しました。',
              onDragEnd: ({ active, over }) =>
                over
                  ? `${getPositionLabel(active.id)}を${getPositionLabel(over.id)}の位置に移動しました。`
                  : '並び替えを取り消しました。',
              onDragCancel: ({ active }) =>
                `${getPositionLabel(active.id)}の移動を取り消しました。`,
            },
          }}
        >
          <SortableContext
            items={pendingCards.map((card) => card.id)}
            strategy={
              isMobile ? verticalListSortingStrategy : rectSortingStrategy
            }
          >
            <ol className="grid min-h-0 flex-1 grid-cols-1 gap-2 overflow-y-auto overscroll-contain px-4 pb-4 sm:grid-cols-2 sm:gap-4 sm:px-1 lg:grid-cols-3">
              {pendingCards.map((item, index) => (
                <CardTypeMessageSortableCard
                  key={item.id}
                  item={item}
                  index={index}
                  templateId={templateId}
                  disabled={disabled}
                  mobile={isMobile}
                  previewOpen={previewCardId === item.id}
                  cardCount={pendingCards.length}
                  onTogglePreview={() =>
                    setPreviewCardId((current) =>
                      current === item.id ? null : item.id,
                    )
                  }
                  onMoveToPosition={(position) =>
                    handleMoveToPosition(item.id, position)
                  }
                />
              ))}
            </ol>
          </SortableContext>
          {isMobile && typeof document !== 'undefined'
            ? createPortal(
                <DragOverlay>
                  {activeCard ? (
                    <MobileDragOverlayCard
                      item={activeCard}
                      index={activeCardIndex}
                    />
                  ) : null}
                </DragOverlay>,
                document.body,
              )
            : null}
        </DndContext>

        <p className="sr-only" aria-live="polite">
          {moveAnnouncement}
        </p>

        <div
          data-testid="reorder-modal-footer"
          className="flex shrink-0 justify-end gap-2 border-t border-neutral-200 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-0 sm:pt-4"
        >
          <Button type="button" variant="outline" onClick={onClose}>
            {strings.templateModalCancel}
          </Button>
          <Button
            type="button"
            disabled={disabled}
            onClick={() => onApply(pendingCards.map((card) => card.id))}
          >
            {strings.templateModalApply}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
